import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, FileText, Layers, Plus, AlertTriangle, Loader2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

/**
 * Build Vision Deck Picker Modal — Document 2 · Phase 4.2
 *
 * Three-tab picker shown after the user clicks "Build Vision Deck" on a
 * generated Vision Proposal:
 *   1. Use a Template — preset templates (static registry)
 *   2. Start from Existing Deck — clone one of the user's decks (live fetch)
 *   3. Blank — default modern-executive
 *
 * Selection is POSTed to /api/vision-decks/from-proposal which creates a
 * new deck with status='draft'. Source proposals and source decks are
 * NEVER modified.
 */

// Template registry — MUST match the editor's TEMPLATE_STYLES keys in
// /app/frontend/src/components/vision-deck/constants.js. Picking an id
// the editor doesn't know about used to cause a runtime crash on open
// (Cannot read properties of undefined). Kept as a curated list (3
// options) rather than a dynamic fetch since the editor itself ships
// templates as code, not data.
const TEMPLATES = [
  {
    id: 'modern-executive',
    name: 'Modern Executive',
    description: 'Clean, type-led layout. Best for finance, ops, leadership audiences.',
    accent: 'from-stone-900 to-stone-700',
  },
  {
    id: 'contemporary-hospitality',
    name: 'Contemporary Hospitality',
    description: 'Warm tones and lifestyle imagery for hotels and resorts.',
    accent: 'from-amber-700 to-amber-900',
  },
  {
    id: 'corporate-prestige',
    name: 'Corporate Prestige',
    description: 'Dark backgrounds and bold typography for executive presentations.',
    accent: 'from-slate-700 to-slate-900',
  },
];

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

export default function BuildDeckPickerModal({ open, onOpenChange, onBuild, proposalSummary }) {
  const api = useApi();
  const [activeTab, setActiveTab] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState('modern-executive');
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [decks, setDecks] = useState(null); // null = not loaded, [] = loaded empty
  const [decksLoading, setDecksLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch user's existing decks the first time the modal opens
  useEffect(() => {
    if (!open || decks !== null) return;
    let cancelled = false;
    setDecksLoading(true);
    (async () => {
      try {
        const data = await api.get('/vision-decks');
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setDecks(list);
      } catch (e) {
        if (!cancelled) setDecks([]);
      } finally {
        if (!cancelled) setDecksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleBuild = async () => {
    if (submitting) return;
    let payload;
    if (activeTab === 'template') {
      payload = { mode: 'template', template_id: selectedTemplate };
    } else if (activeTab === 'existing') {
      if (!selectedDeck) return;
      payload = { mode: 'existing', source_deck_id: selectedDeck };
    } else {
      payload = { mode: 'blank' };
    }
    if (typeof onBuild === 'function') {
      setSubmitting(true);
      try {
        await onBuild(payload);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const buildDisabled =
    submitting || (activeTab === 'existing' && !selectedDeck);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="build-deck-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#B89B5E]" />
            Build Vision Deck
          </DialogTitle>
          <DialogDescription>
            Turn this proposal into a Vision Deck draft. The deck is created as a draft — you'll review and publish it yourself.
          </DialogDescription>
        </DialogHeader>

        {proposalSummary && (
          <div className="rounded-sm border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
            <span className="font-semibold text-stone-800">Source proposal:</span>{' '}
            {proposalSummary}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="template" data-testid="tab-template">
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              Use Template
            </TabsTrigger>
            <TabsTrigger value="existing" data-testid="tab-existing">
              <Layers className="h-3.5 w-3.5 mr-1.5" />
              From Existing
            </TabsTrigger>
            <TabsTrigger value="blank" data-testid="tab-blank">
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Blank
            </TabsTrigger>
          </TabsList>

          {/* Use Template */}
          <TabsContent value="template" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="template-grid">
              {TEMPLATES.map((t) => {
                const selected = selectedTemplate === t.id;
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setSelectedTemplate(t.id)}
                    data-testid={`template-card-${t.id}`}
                    className={`text-left rounded-sm border-2 transition-all overflow-hidden group ${
                      selected
                        ? 'border-[#B89B5E] ring-2 ring-[#B89B5E]/20'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div className={`h-20 bg-gradient-to-br ${t.accent} flex items-end p-3`}>
                      <div className="text-white text-xs font-medium opacity-90">
                        {t.name}
                      </div>
                    </div>
                    <div className="p-3 bg-white">
                      <p className="text-xs text-stone-600 leading-snug">{t.description}</p>
                      {selected && (
                        <Badge className="mt-2 bg-[#B89B5E] text-white text-[10px] uppercase tracking-wide">
                          Selected
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </TabsContent>

          {/* From Existing Deck */}
          <TabsContent value="existing" className="mt-4">
            {decksLoading && (
              <div className="flex items-center justify-center py-8 text-sm text-stone-500" data-testid="existing-deck-loading">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading your decks…
              </div>
            )}
            {!decksLoading && decks && decks.length === 0 && (
              <div
                className="rounded-sm border-2 border-dashed border-stone-200 p-6 text-center text-sm text-stone-500"
                data-testid="existing-deck-empty"
              >
                You don't have any decks yet. Use a Template or Blank to start your first one.
              </div>
            )}
            {!decksLoading && decks && decks.length > 0 && (
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="existing-deck-list">
                {decks.map((d) => {
                  const selected = selectedDeck === d.id;
                  return (
                    <button
                      type="button"
                      key={d.id}
                      onClick={() => setSelectedDeck(d.id)}
                      data-testid={`existing-deck-${d.id}`}
                      className={`w-full text-left flex items-center justify-between gap-3 rounded-sm border-2 px-3 py-3 transition-all ${
                        selected
                          ? 'border-[#B89B5E] bg-[#B89B5E]/5 ring-2 ring-[#B89B5E]/20'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-800 truncate">
                          {d.name || d.title || 'Untitled Deck'}
                        </p>
                        <p className="text-xs text-stone-500 mt-0.5">
                          {(d.page_count || (d.pages && d.pages.length) || 0)} pages
                          {d.updated_at ? ` · updated ${formatDate(d.updated_at)}` : ''}
                          {d.status ? ` · ${d.status}` : ''}
                        </p>
                      </div>
                      {selected && (
                        <Badge className="bg-[#B89B5E] text-white text-[10px] uppercase tracking-wide flex-shrink-0">
                          Selected
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="mt-3 text-[11px] text-stone-500">
              Cloning a deck creates a new draft — your source deck is never modified.
            </p>
          </TabsContent>

          {/* Blank */}
          <TabsContent value="blank" className="mt-4">
            <div
              className="rounded-sm border-2 border-dashed border-stone-200 p-6 text-center"
              data-testid="blank-card"
            >
              <Plus className="h-8 w-8 text-stone-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-stone-800">Start from a blank deck</p>
              <p className="text-xs text-stone-500 mt-1">
                Uses the default Modern Executive layout with no pre-fills beyond your proposal content.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Draft warning */}
        <div className="mt-3 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            <span className="font-semibold">Draft only.</span> The deck will be created as a draft. You'll review it in the Vision Deck Editor before publishing or sending to a recipient.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="cancel-build-deck"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleBuild}
            disabled={buildDisabled}
            data-testid="confirm-build-deck"
            className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Building…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Build Deck Draft
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
